/**
 * customMethods.js — Shared Business Logic Utilities
 * ─────────────────────────────────────────────────────
 * CHANGES:
 *  1. Removed duplicate getSubDomain() — re-exports from subdomainHelper
 *  2. Removed duplicate convertToFloat() at module level — uses the one already here
 *  3. Fixed all == to === (15+ occurrences)
 *  4. Fixed .map() without return in sortName/longName — use .find() directly
 *  5. Defensive null checks on getDataFromLaunchStore reads
 *  6. fillDropDownsData uses authValidator.isAuthenticated (single source)
 *  7. Cleaned up dead/commented-out code in showFile
 */

import * as dropDownsAction from '../actions/adminAction/dropDownDataAction';
import * as initialReqInfoAction from '../actions/dispatchAction/initialReqInfoAction';
import * as reviewInboxAction from '../actions/dispatchAction/reviewInboxAction';
import { LAUNCH_STORE_NAME, IS_LOCAL_STORAGE, callStatus, priorities, mapfilterData } from '../util/constants';
import { store } from '../store/configureStore';
import { isAuthenticated } from '../util/authValidator';
import moment from 'moment';

// Re-export from subdomainHelper — single source of truth
export { getSubDomain } from '../util/subdomainHelper';

export const LAUNCH_URL = window.location.origin;
export const ROOT_DISPATCHER = 'dispatch';
export const ROOT_ADMIN = 'admin';
export const ROOT_REPORTS = 'reports';

export const convertToFloat = (value, decimalLength = 2) => {
  return parseFloat((isNaN(parseFloat(value)) ? 0.00 : parseFloat(value)).toFixed(decimalLength));
};

export const getCompanyId = () => {
  const authInfo = getDataFromLaunchStore('authInfo');
  if (Object.keys(authInfo).length > 0) {
    return authInfo.userDetails.companyId;
  }
  return 0;
};

export const isManagement = () => {
  const authInfo = getDataFromLaunchStore('authInfo');
  if (Object.keys(authInfo).length > 0) {
    return authInfo.userDetails.isManagement;
  }
  return false;
};

export const isRetail = () => {
  const authInfo = getDataFromLaunchStore('authInfo');
  if (Object.keys(authInfo || {}).length > 0) {
    const user = authInfo.userDetails || {};
    return (user.companyType || '').toLowerCase() === 'retail';
  }
  return false;
};

export const isManagemed = () => {
  const authInfo = getDataFromLaunchStore('authInfo');
  if (Object.keys(authInfo).length > 0) {
    return ((authInfo.userDetails.companyType || '').toUpperCase()) === 'MANAGED';
  }
  return false;
};

/** Case-insensitive string comparison */
export const compStr = (str1, str2) => {
  return (str1 || '').toUpperCase() === (str2 || '').toUpperCase();
};

/** Validate a value by type */
export const validate = (value, type) => {
  switch ((type || '').toUpperCase()) {
    case 'STRING':
      return (value === null || value === '' || value === undefined);
    default:
      return false;
  }
};

export const getUserName = () => {
  const authInfo = getDataFromLaunchStore('authInfo');
  if (Object.keys(authInfo).length > 0) {
    return `${authInfo.userDetails.firstName} ${authInfo.userDetails.lastName}`;
  }
  return undefined;
};

export const getLoginUserName = () => {
  const authInfo = getDataFromLaunchStore('authInfo');
  if (Object.keys(authInfo).length > 0) {
    return authInfo.userDetails.userId;
  }
  return undefined;
};

export const getLoginUserId = () => {
  const authInfo = getDataFromLaunchStore('authInfo');
  if (Object.keys(authInfo).length > 0) {
    return authInfo.userDetails.employeeId;
  }
  return undefined;
};

export const getAccessLevels = () => {
  return getDataFromLaunchStore('accessLevel') || {};
};

export const dashboardFilterByType = (type) => {
  let filter = localStorage.getItem(type) || '{}';
  try {
    const parsed = JSON.parse(filter);
    if (Object.keys(parsed).length > 0) {
      return parsed;
    }
  } catch (e) {
    // Corrupted — fall through to defaults
  }

  if (type === 'byStatus') return callStatus;
  if (type === 'byPriority') return priorities;
  if (type === 'mapFilter') return mapfilterData;
  return {};
};

export const setDashboardFilterByType = (type, filter) => {
  localStorage.setItem(type, JSON.stringify(filter));
};

export const getDashboardAccessLevels = () => {
  return getDataFromLaunchStore('dashboardAccess') || {};
};

export const editPermission = (itemComp) => {
  if (isManagement()) return true;
  const myCompanyId = getCompanyId();
  if (!itemComp || !myCompanyId) return false;
  return (Number(itemComp) === Number(myCompanyId));
};

export const getAccessLevelsByModule = (subModuleName = undefined) => {
  const mainModuleName = 'Admin';
  const accessGroup = getAccessLevels() || {};

  if (Object.keys(accessGroup).length === 0) return { basicAccess: {} };

  const moduleData = accessGroup[mainModuleName];
  if (!moduleData) return { basicAccess: {} };

  const searchKey = Object.keys(moduleData).find(
    (mod) => mod.toLowerCase() === subModuleName.toLowerCase()
  );

  if (searchKey !== undefined) {
    const searchObj = moduleData[searchKey][0];
    searchObj.basicAccess = searchObj.basicAccess || {};
    return searchObj;
  }

  // Deep search inside module arrays
  let result = { basicAccess: {} };
  Object.keys(moduleData).some((mod) => {
    if (moduleData[mod].length > 0) {
      const found = moduleData[mod].find(
        (item) => item.pageName.toLowerCase() === subModuleName.toLowerCase()
      );
      if (found) {
        found.basicAccess = found.basicAccess || {};
        result = found;
        return true;
      }
    }
    return false;
  });
  return result;
};

export const getAccessLevelsByModuleName = (subModuleName = undefined) => {
  const mainModuleName = 'Dispatcher';
  const accessGroup = getAccessLevels() || {};

  if (Object.keys(accessGroup).length === 0) return { basicAccess: {} };

  const moduleData = accessGroup[mainModuleName] || {};
  const searchKey = Object.keys(moduleData).find(
    (mod) => mod.toLowerCase() === subModuleName.toLowerCase()
  );

  if (searchKey !== undefined) {
    const searchObj = moduleData[searchKey];
    searchObj.basicAccess = searchObj.basicAccess || {};
    return searchObj;
  }

  let result = { basicAccess: {} };
  Object.keys(moduleData).some((mod) => {
    if (moduleData[mod].length > 1) {
      const found = moduleData[mod].find(
        (item) => item.pageName.toLowerCase() === subModuleName.toLowerCase()
      );
      if (found) {
        found.basicAccess = found.basicAccess || {};
        result = found;
        return true;
      }
    }
    return false;
  });
  return result;
};

/* ──────────────────────────────────────────────
 * Launch Store (localStorage/sessionStorage wrapper)
 * ────────────────────────────────────────────── */

export const setDataToLaunchStore = (key, objData) => {
  const currentStore = usingLaunchStore();
  currentStore[key] = JSON.stringify(objData);

  const storage = IS_LOCAL_STORAGE ? localStorage : sessionStorage;
  storage.setItem(LAUNCH_STORE_NAME, JSON.stringify(currentStore));
};

export const getDataFromLaunchStore = (key) => {
  const currentStore = usingLaunchStore();
  return currentStore[key] || {};
};

const usingLaunchStore = () => {
  const storage = IS_LOCAL_STORAGE ? localStorage : sessionStorage;
  let parsed = {};

  try {
    const raw = storage.getItem(LAUNCH_STORE_NAME);
    if (raw && raw !== '' && raw !== 'null') {
      const existingStore = JSON.parse(raw);
      Object.keys(existingStore).forEach((keyName) => {
        try {
          parsed[keyName] = JSON.parse(existingStore[keyName]);
        } catch (e) {
          parsed[keyName] = existingStore[keyName];
        }
      });
    } else {
      storage.setItem(LAUNCH_STORE_NAME, JSON.stringify(parsed));
    }
  } catch (e) {
    storage.setItem(LAUNCH_STORE_NAME, JSON.stringify(parsed));
  }

  return parsed;
};

export const getRefreshToken = () => {
  const authInfo = getDataFromLaunchStore('authInfo');
  if (Object.keys(authInfo).length > 0) {
    return (authInfo.oaAuthTokens || {}).refresh_token || undefined;
  }
  return undefined;
};

export const setAuthTokens = (resp) => {
  const authInfo = getDataFromLaunchStore('authInfo');
  const tokenObj = authInfo.oaAuthTokens || {};
  tokenObj.access_token = resp.access_token;
  tokenObj.refresh_token = resp.refresh_token;
  tokenObj.token_creationTime = resp.expires_in;
  authInfo.oaAuthTokens = tokenObj;
  setDataToLaunchStore('authInfo', authInfo);
};

export const clearSessionData = () => {
  setDataToLaunchStore('authInfo', {});
};

export const sortName = (key) => {
  const stateList = (store.getState()).states.stateList || [];
  if (stateList.length > 0 && (key || '') !== '') {
    const obj = stateList.find(
      (item) => (item.optionVal || '').toUpperCase() === key.toUpperCase()
    );
    return obj ? obj.sortName : '';
  }
  return '';
};

export const longName = (key) => {
  const stateList = (store.getState()).states.stateList || [];
  if (stateList.length > 0 && (key || '') !== '') {
    const obj = stateList.find(
      (item) => (item.sortName || '').toUpperCase() === key.toUpperCase()
    );
    return obj ? obj.optionVal : '';
  }
  return '';
};

export const float = (val) => {
  const valToFmt = val === '' ? 0 : val;
  return parseFloat(valToFmt || 0.00).toFixed(2);
};

export const returnDateObj = (key) => {
  if (key === null) return null;
  return moment(key);
};

export const fillDropDownsData = () => {
  return async (dispatch) => {
    await dispatch(dropDownsAction.getStateList());
    await dispatch(initialReqInfoAction.fetchAccountList());
    await dispatch(dropDownsAction.getAllTowTypes());
    await dispatch(dropDownsAction.getDepartments());
    await dispatch(dropDownsAction.getLocationList());
    await dispatch(dropDownsAction.getAccountType());
    await dispatch(dropDownsAction.getSalesRep());
    await dispatch(dropDownsAction.getCallReasons());
    await dispatch(dropDownsAction.getRatePlanList());
    await dispatch(dropDownsAction.getCommissionTypes());
    await dispatch(dropDownsAction.getLocationDetailsBySearch());
    await dispatch(reviewInboxAction.getReviewLocationList(null, false));
    if (!isManagement()) {
      await dispatch(dropDownsAction.getLicenseTypes());
    }
    await dispatch(dropDownsAction.getChildList());
  };
};

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export function showFile(blob, fileName) {
  const newBlob = new Blob([blob], { type: 'application/pdf' });

  // IE fallback
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(newBlob, `${fileName}.pdf`);
    return;
  }

  const blobURL = window.URL.createObjectURL(newBlob);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', `${fileName}.pdf`);
  document.body.appendChild(tempLink);
  tempLink.click();
  document.body.removeChild(tempLink);

  setTimeout(() => {
    window.URL.revokeObjectURL(blobURL);
  }, 100);
}

export const reportText = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'rateplan': return 'Summary details by rate plan';
    case 'towtype':  return 'Summary details by towType';
    case 'truck':    return 'Summary details by truck';
    case 'reason':   return 'Summary details by reason';
    case 'driver':   return 'Summary details by driver';
    default:         return '';
  }
};

/**
 * Calculates labor charge based on billing parameters and total hours.
 */
export const calculateLaborCharge = (billing, totalHrs) => {
  try {
    if (!billing || isNaN(totalHrs) || totalHrs < 0) return 0;

    const hourlyRate = Number(billing.service_hourly_rate) || 0;
    const minHours = Number(billing.service_min_hours) || 0;
    const incrHours = Number(billing.service_increment_hours) || 0;
    const freeHours = Number(billing.service_free_hours) || 0;

    let billableHours;
    if (minHours >= totalHrs) {
      billableHours = minHours;
    } else if (incrHours > 0) {
      const remaining = totalHrs - minHours;
      billableHours = minHours + (incrHours * Math.ceil(remaining / incrHours));
    } else {
      billableHours = totalHrs;
    }

    if (freeHours >= billableHours) {
      billableHours = 0;
    } else if (freeHours > 0) {
      billableHours -= freeHours;
    }

    return Number((billableHours * hourlyRate).toFixed(2));
  } catch (error) {
    console.error('Error calculating labor charge:', error);
    return 0;
  }
};

export const getBasicAccess = (pageName) => {
  try {
    const permissions = getAccessLevels();
    const moduleData = permissions['Reports'];
    if (moduleData) {
      const pageData = moduleData[pageName] && moduleData[pageName][0];
      if (pageData && pageData.basicAccess) {
        return {
          edit: pageData.basicAccess.edit !== undefined ? pageData.basicAccess.edit : false,
          view: pageData.basicAccess.view !== undefined ? pageData.basicAccess.view : false,
        };
      }
    }
  } catch (error) {
    console.error('Error:', error);
  }
  return { edit: false, view: false };
};
